import _flatMap from "lodash/flatMap";
import _orderBy from "lodash/orderBy";
import * as queryString from "query-string";
import * as router from "react-router-redux";
import { createSelector } from "reselect";
import {
    AGE,
    EDUCATION,
    GENDER,
    INCOME
} from "../components/Results/QuoteCell";
import { actionPrefix } from "./constants";
import { getPageResults } from "./results";

const prefix = `${actionPrefix}/drivers`;

export const SENTIMENT = {
    positive: "positive",
    negative: "negative"
};

export const DEFAULT_SEGMENTS = [
    ...AGE.segments,
    ...GENDER.segments,
    ...INCOME.segments,
    ...EDUCATION.segments
].map(segment => segment.id);

export const B2B_VALUE_DICT = {
    // B2B Age
    "Less than 1 year": { options: ["Less than 1"], sortOrder: 0 },
    "1-2 years": { options: ["1-2 years", "1 - 2 years"], sortOrder: 1 },
    "3-5 years": { options: ["3-5 years", "3 - 5 years"], sortOrder: 2 },
    "6-9 years": { options: ["6-9 years", "6 - 9 years"], sortOrder: 3 },
    "10+ years": { options: ["10+ years"], sortOrder: 4 },
    "All ages": {
        options: [
            "Less than 1",
            "1-2 years",
            "1 - 2 years",
            "3-5 years",
            "3 - 5 years",
            "6-9 years",
            "6 - 9 years",
            "10+ years"
        ]
    },
    //B2B Revenue
    "<$500k": {
        options: [
            "Less than $50,000",
            "$50,000 to less than $250,000",
            "$250,000 to less than $500,000",
            "Under $500k"
        ],
        sortOrder: 0
    },
    "$500k - <$1M": {
        options: [
            "$500,000 to less than $750,000",
            "$750,000 to less than $1 million",
            "$500k - $1 million"
        ],
        sortOrder: 3
    },
    "$1M - <$2M": {
        options: ["$1 million to less than $2 million", "$1 - $2 million"],
        sortOrder: 4
    },
    "$2M - <$5M": {
        options: [
            "$2 million to less than $5 million",
            "$2 - $3 million",
            "$3 - $5 million"
        ],
        sortOrder: 5
    },
    "$5M - <$10M": {
        options: ["$5 million to less than $10 million", "$5 - $10 million"],
        sortOrder: 6
    },
    "$10M - <$25M": {
        options: [
            "$10 million to less than $25 million",
            "Greater than $10 million"
        ],
        sortOrder: 7
    },
    "$25M - <$50M": {
        options: [
            "$25 million to less than $50 million",
            "Greater than $10 million"
        ],
        sortOrder: 8
    },
    "$50M - <$100M": {
        options: [
            "$50 million to less than $100 million",
            "Greater than $10 million"
        ],
        sortOrder: 9
    },
    "$100M - <$500M": {
        options: [
            "$100 million to less than $500 million",
            "Greater than $10 million"
        ],
        sortOrder: 10
    },
    "$500M - <$1B": {
        options: [
            "$500 million to less than $1 billion",
            "Greater than $10 million"
        ],
        sortOrder: 11
    },
    "$1B - <$10B": {
        options: [
            "$1 billion to less than $10 billion",
            "Greater than $10 million"
        ],
        sortOrder: 12
    },
    "$10B+": {
        options: ["Over $10 billion"],
        sortOrder: 13
    },
    "All revenues": {
        options: [
            "Less than $50,000",
            "$50,000 to less than $250,000",
            "$250,000 to less than $500,000",
            "Under $500k",
            "$500,000 to less than $750,000",
            "$750,000 to less than $1 million",
            "$500k - $1 million",
            "$1 million to less than $2 million",
            "$1 - $2 million",
            "$2 million to less than $5 million",
            "$2 - $3 million",
            "$3 - $5 million",
            "$5 million to less than $10 million",
            "$5 - $10 million",
            "$10 million to less than $25 million",
            "$25 million to less than $50 million",
            "$50 million to less than $100 million",
            "$100 million to less than $500 million",
            "$500 million to less than $1 billion",
            "$1 billion to less than $10 billion",
            "Greater than $10 million",
            "Over $10 billion"
        ]
    },
    //B2B Role
    "Entry Level": { options: ["Entry Level"], sortOrder: 0 },
    "Home Based Business Owner": {
        options: ["Home Based Business Owner"],
        sortOrder: 1
    },
    "Front Line Management": {
        options: ["Front Line Management"],
        sortOrder: 2
    },
    "Experienced: Professional or Non-Management": {
        options: [
            "Experienced: Professional or Non-Management",
            "Associate",
            "Contractor"
        ],
        sortOrder: 3
    },
    "Middle Management": {
        options: [
            "Middle Management (Director / Divisional / Department Head)",
            "Manager",
            "Director",
            "Executive (CXO)",
            "Executive (VP, EVP)"
        ],
        sortOrder: 4
    },
    "Senior Management": {
        options: ["Senior Management (CEO / VP / Managing Director)"],
        sortOrder: 5
    },
    "Business Owner": { options: ["Business Owner"], sortOrder: 6 },
    Other: { options: ["Other"], sortOrder: 7 },
    "Owner / Partner": { options: ["Owner / Partner"], sortOrder: 8 },

    "All roles": {
        options: [
            "Entry Level",
            "Home Based Business Owner",
            "Front Line Management",
            "Experienced: Professional or Non-Management",
            "Associate",
            "Contractor",
            "Middle Management (Director / Divisional / Department Head)",
            "Manager",
            "Director",
            "Executive (CXO)",
            "Executive (VP, EVP)",
            "Senior Management (CEO / VP / Managing Director)",
            "Business Owner",
            "Owner / Partner",
            "Other"
        ]
    },
    // B2B Size
    "1": { options: ["1"], sortOrder: 0 },
    "2-10": { options: ["2 to 5", "6 to 9", "2-10"], sortOrder: 1 },
    "10-49": { options: ["10 to 49", "11-50"], sortOrder: 3 },
    "50-99": { options: ["50 to 99", "51-100"], sortOrder: 4 },
    "100-249": { options: ["100 to 249", "101-200"], sortOrder: 5 },
    "250-499": { options: ["250 to 499", "201-500"], sortOrder: 6 },
    "500-999": { options: ["500 to 999", "500 - 1000"], sortOrder: 7 },
    "1,000-2,499": { options: ["1,000 to 2,499", "1000+"], sortOrder: 8 },
    "2,500-4,999": { options: ["2,500 to 4,999", "1000+"], sortOrder: 9 },
    "5,000-9,999": { options: ["5,000 to 9,999", "1000+"], sortOrder: 10 },
    "10,000-24,999": {
        options: ["10,000 to 24,999", "1000+"],
        sortOrder: 11
    },
    "25,000 or more": {
        options: ["25,000 or more", "25,000+"],
        sortOrder: 12
    },
    "All sizes": {
        options: [
            "1",
            "2 to 5",
            "6 to 9",
            "2-10",
            "10 to 49",
            "11-50",
            "50 to 99",
            "51-100",
            "100 to 249",
            "101-200",
            "250 to 499",
            "201-500",
            "500 to 999",
            "500 - 1000",
            "1,000 to 2,499",
            "2,500 to 4,999",
            "5,000 to 9,999",
            "10,000 to 24,999",
            "25,000 or more",
            "25,000+",
            "1000+"
        ]
    }
};

export const B2B_DICT = {
    // B2B Age
    "Less than 1": 74,
    "1-2 years": 75,
    "1 - 2 years": 75,
    "1-3 years": 105,
    "1 - 3 years": 105,
    "3-5 years": 76,
    "3 - 5 years": 76,
    "6-9 years": 77,
    "6 - 9 years": 77,
    "7-10 years": 106,
    "7 - 10 years": 106,
    "10+ years": 78,

    //B2B Revenue
    "Less than $50,000": 36,
    "$50,000 to less than $250,000": 37,
    "$250,000 to less than $500,000": 38,
    "Under $500k": 107,
    "$500,000 to less than $750,000": 39,
    "$750,000 to less than $1 million": 40,
    "$500k - $1 million": 108,
    "$1 million to less than $2 million": 41,
    "$1 - $2 million": 41,
    "$2 million to less than $5 million": 42,
    "$2 - $3 million": 42,
    "$3 - $5 million": 42,
    "$5 million to less than $10 million": 43,
    "$5 - $10 million": 43,
    "$10 million to less than $25 million": 44,
    "Greater than $10 million": 110,
    "$25 million to less than $50 million": 45,
    "$50 million to less than $100 million": 46,
    "$100 million to less than $500 million": 47,
    "$500 million to less than $1 billion": 48,
    "$1 billion to less than $10 billion": 49,
    "Over $10 billion": 50,

    //B2B Role
    "Business Owner": 52,
    "Senior Management (CEO / VP / Managing Director)": 53,
    "Middle Management (Director / Divisional / Department Head)": 54,
    Manager: 54,
    Director: 54,
    "Executive (CXO)": 54,
    "Executive (VP, EVP)": 54,
    "Front Line Management": 55,
    "Experienced: Professional or Non-Management": 56,
    Associate: 56,
    Contractor: 56,
    "Entry Level": 57,
    "Home Based Business Owner": 58,
    Other: 59,
    "Owner / Partner": 110,

    // B2B Size
    "1": 60,
    "2 to 5": 61,
    "6 to 9": 62,
    "2-10": 111,
    "10 to 49": 63,
    "11-50": 63,
    "50 to 99": 64,
    "51-100": 64,
    "100 to 249": 65,
    "101-200": 65,
    "250 to 499": 66,
    "201-500": 66,
    "500 to 999": 67,
    "500 - 1000": 67,
    "1000+": 112,
    "1,000 to 2,499": 68,
    "2,500 to 4,999": 69,
    "5,000 to 9,999": 70,
    "10,000 to 24,999": 71,
    "25,000 or more": 72,
    "25,000+": 72
};

const DEFAULT_B2B_FILTERS = [...Object.keys(B2B_VALUE_DICT)].filter(
    key => !key.includes("All")
); // filter out the mappings for the catchall filters which are only used on the heatmap page

const actions = {
    UPDATE_SEARCH_QUERY: `${prefix}/UPDATE_SEARCH_QUERY`,
    CLEAR_SEARCH_QUERY: `${prefix}/CLEAR_SEARCH_QUERY`,

    ADD_SEGMENT_FILTER: `${prefix}/ADD_SEGMENT_FILTER`,
    REMOVE_SEGMENT_FILTER: `${prefix}/REMOVE_SEGMENT_FILTER`,

    ADD_SENTIMENT_FILTER: `${prefix}/ADD_SENTIMENT_FILTER`,
    REMOVE_SENTIMENT_FILTER: `${prefix}/REMOVE_SENTIMENT_FILTER`,

    ADD_B2B_FILTER: `${prefix}/ADD_B2B_FILTER`,
    REMOVE_B2B_FILTER: `${prefix}/REMOVE_B2B_FILTER`,

    RESET_FILTERS: `${prefix}/RESET_FILTERS`
};

export function getSelectedDriver(state) {
    return state.queryString["driver"] || "All";
}

export const getDriversForPage = createSelector(
    [getPageResults],
    pageResults => {
        return pageResults ? pageResults.drivers : null;
    }
);

export const getDriverQuotes = createSelector(
    [getDriversForPage, getSelectedDriver],
    (drivers, selectedDriver) => {
        if (!drivers) {
            return [];
        }
        if (selectedDriver !== "All") {
            drivers = drivers.filter(({ driver }) => driver === selectedDriver);
        }
        return _orderBy(
            _flatMap(drivers, driver => driver.quotes),
            ["quoteScore"],
            ["desc"]
        );
    }
);

export function selectDriver(driver) {
    return async (dispatch, getState) => {
        const queryParams = {
            ...getState().queryString,
            driver
        };

        return dispatch(
            router.replace({
                search: queryString.stringify(queryParams)
            })
        );
    };
}

export function updateSearchQuery(query) {
    return {
        type: actions.UPDATE_SEARCH_QUERY,
        payload: { query }
    };
}

export function clearSearchQuery() {
    return {
        type: actions.CLEAR_SEARCH_QUERY
    };
}

export function addSegmentFilter(value) {
    return {
        type: actions.ADD_SEGMENT_FILTER,
        payload: { value }
    };
}

export function removeSegmentFilter(value) {
    return {
        type: actions.REMOVE_SEGMENT_FILTER,
        payload: { value }
    };
}

export function addSentimentFilter(value) {
    return {
        type: actions.ADD_SENTIMENT_FILTER,
        payload: { value }
    };
}

export function removeSentimentFilter(value) {
    return {
        type: actions.REMOVE_SENTIMENT_FILTER,
        payload: { value }
    };
}

export function addB2bFilter(value) {
    return {
        type: actions.ADD_B2B_FILTER,
        payload: { value }
    };
}

export function removeB2bFilter(value) {
    return {
        type: actions.REMOVE_B2B_FILTER,
        payload: { value }
    };
}

export function resetFilters() {
    return {
        type: actions.RESET_FILTERS
    };
}

const INITIAL_STATE = {
    searchQuery: "",
    selectedSegments: DEFAULT_SEGMENTS,
    selectedSentiments: [SENTIMENT.negative, SENTIMENT.positive],
    selectedB2bFilters: DEFAULT_B2B_FILTERS
};

export function reducer(state = INITIAL_STATE, action) {
    switch (action.type) {
        case actions.UPDATE_SEARCH_QUERY:
            return {
                ...state,
                searchQuery: action.payload.query
            };
        case actions.CLEAR_SEARCH_QUERY:
            return {
                ...state,
                searchQuery: ""
            };

        case actions.ADD_SEGMENT_FILTER:
            return {
                ...state,
                selectedSegments: [
                    ...state.selectedSegments,
                    action.payload.value
                ]
            };

        case actions.REMOVE_SEGMENT_FILTER:
            return {
                ...state,
                selectedSegments: state.selectedSegments.filter(
                    value => value !== action.payload.value
                )
            };

        case actions.ADD_SENTIMENT_FILTER:
            return {
                ...state,
                selectedSentiments: [
                    ...state.selectedSentiments,
                    action.payload.value
                ]
            };

        case actions.REMOVE_SENTIMENT_FILTER:
            return {
                ...state,
                selectedSentiments: state.selectedSentiments.filter(
                    value => value !== action.payload.value
                )
            };

        case actions.ADD_B2B_FILTER:
            return {
                ...state,
                selectedB2bFilters: [
                    ...state.selectedB2bFilters,
                    action.payload.value
                ]
            };

        case actions.REMOVE_B2B_FILTER:
            return {
                ...state,
                selectedB2bFilters: state.selectedB2bFilters.filter(
                    value => value !== action.payload.value
                )
            };

        case actions.RESET_FILTERS:
            return {
                ...state,
                selectedSegments: INITIAL_STATE.selectedSegments,
                selectedSentiments: INITIAL_STATE.selectedSentiments,
                selectedB2bFilters: INITIAL_STATE.selectedB2bFilters
            };

        default:
            return state;
    }
}



// WEBPACK FOOTER //
// ./src/modules/drivers.js